// @flow
import React, { Suspense } from "react";
import { ProfileTicketPersonRow } from "../Profile/ProfileTicket";
import {
  useAuth,
  useFirestore,
  useFirestoreDocData,
  useFirestoreDocDataOnce,
} from "reactfire";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import formatDates from "../../common/formatDates";
import moment from "moment";
import Skeleton from "@material-ui/lab/Skeleton";
import ProfileTicketCompanions from "../Profile/ProfileTicketCompanions";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import TicketDetailsPayment from "./TicketDetailsPayment";
import PageSubHeading from "../../components/PageSubHeading";
import TicketReservationHints from "../TicketReservation/TicketReservationHints";
import Button from "@material-ui/core/Button";
import type { Game, TicketReservation, UserData } from "../../common/types";
import TicketPayButton from "./TicketPayButton";
import Divider from "@material-ui/core/Divider";
type Props = {
  gameId: string,
  query: any,
};

const DOT_SIZE = 16;
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    wrapper: {
      background: "#5D5CDE",
      boxShadow: "0 0 0 0",
      borderTopLeftRadius: 9,
      borderTopRightRadius: 9,
      borderBottomRightRadius: 0,
      borderBottomLeftRadius: 0,
      color: "white",
      borderBottomWidth: 2,
      borderBottomStyle: "dashed",
      borderBottomColor: theme.palette.background.paper,
      paddingLeft: 0,
      paddingRight: 0,
    },
    leftDot: {
      display: "block",
      backgroundColor: theme.palette.background.paper,
      position: "absolute",
      width: DOT_SIZE,
      height: DOT_SIZE,
      bottom: -(DOT_SIZE / 2),
      left: -(DOT_SIZE / 2),
      borderRadius: DOT_SIZE,
    },
    rightDot: {
      display: "block",
      backgroundColor: theme.palette.background.paper,
      position: "absolute",
      width: DOT_SIZE,
      height: DOT_SIZE,
      bottom: -(DOT_SIZE / 2),
      right: -(DOT_SIZE / 2),
      borderRadius: DOT_SIZE,
    },
    attachment: {
      background: "#5D5CDE",
      boxShadow: "0 0 0 0",
      borderBottomLeftRadius: 9,
      borderBottomRightRadius: 9,
      borderTopRightRadius: 0,
      borderTopLeftRadius: 0,
      color: "white",
    },
    centerCard: {
      background: "#5D5CDE",
      boxShadow: "0 0 0 0",
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      borderTopRightRadius: 0,
      borderTopLeftRadius: 0,
      color: "white",
      borderBottomWidth: 2,
      borderBottomStyle: "dashed",
      borderBottomColor: theme.palette.background.paper,
      position: "relative",
      overflow: "inherit",
    },
    textSecondary: {
      color: "#CECEF5",
    },
    ticketNumber: {
      fontWeight: "bold",
      textTransform: "uppercase",
    },
    personList: {
      "& > *": {
        marginBottom: theme.spacing(1),
      },
      "& > :last-child": {
        marginBottom: 0,
      },
    },
  })
);

function TicketDetails({ gameId, query }: Props) {
  const auth = useAuth();
  const firestore = useFirestore();
  const classes = useStyles();

  const userId = auth.currentUser.uid;
  const userDataRef = firestore.collection("users").doc(userId);
  const userData: UserData = useFirestoreDocData(userDataRef);

  const gameRef = firestore.collection("games").doc(gameId);
  const game: Game = useFirestoreDocDataOnce(gameRef);

  const ticketReservationRef = gameRef
    .collection("ticketReservations")
    .doc(userId);
  const ticketReservation: TicketReservation = useFirestoreDocData(
    ticketReservationRef
  );

  const withPayment = ticketReservation.paymentMethod === "online";

  if (ticketReservation.id == null) {
    return null;
  }

  const hasCompanions =
    ticketReservation.companionIds != null &&
    ticketReservation.companionIds.length > 0;

  return (
    <>
      {withPayment && (
        <TicketDetailsPayment
          gameId={gameId}
          ticketReservation={ticketReservation}
          query={query}
          ticketReservationRef={ticketReservationRef}
        />
      )}
      <Box mb={2}>
        <Card classes={{ root: classes.wrapper }}>
          <CardContent>
            <Grid container justify="center">
              <Box mb={1} mt={1}>
                <Typography
                  variant={"h3"}
                  className={classes.ticketNumber}
                  align="center"
                  component={"p"}
                >
                  <Typography
                    variant={"h6"}
                    component={"span"}
                    className={classes.textSecondary}
                  >
                    NR&nbsp;
                  </Typography>
                  {ticketReservation.id}
                  <Typography
                    variant={"h6"}
                    component={"span"}
                    className={classes.textSecondary}
                  >
                    &nbsp;&nbsp;&nbsp;
                  </Typography>
                </Typography>
              </Box>
            </Grid>
          </CardContent>
        </Card>
        <Card classes={{ root: classes.centerCard }}>
          <CardContent>
            <Grid
              container
              justify="space-between"
              wrap="nowrap"
              alignItems="center"
            >
              <Grid item>
                <Typography variant="h6" component="p">
                  {formatDates.dayAndDate(game.date)}
                </Typography>
              </Grid>
              <Grid item>
                <Typography align="right" variant="h6" component="p">
                  {formatDates.time(game.date)} Uhr
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              justify="space-between"
              wrap="nowrap"
              alignItems="center"
            >
              <Grid item />
              <Grid item>
                <Typography
                  align="right"
                  variant="body2"
                  className={classes.textSecondary}
                >
                  Einlass ab{" "}
                  {moment(game.date).subtract(60, "minutes").format("HH:mm")}{" "}
                  Uhr
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
          <div className={classes.leftDot} />
          <div className={classes.rightDot} />
        </Card>
        <Card
          classes={{
            root: classes.attachment,
          }}
        >
          <CardContent className={classes.personList}>
            <ProfileTicketPersonRow
              name={userData.name}
              paid={ticketReservation.paymentStatus === "COMPLETE"}
            />
            {hasCompanions && (
              <Suspense
                fallback={
                  <>
                    {ticketReservation.companionIds.map((cid) => (
                      <ProfileTicketPersonRow
                        key={cid}
                        name={<Skeleton width={120} />}
                        paid={ticketReservation.paymentStatus === "COMPLETE"}
                      />
                    ))}
                  </>
                }
              >
                <ProfileTicketCompanions
                  userId={userId}
                  ticketReservation={ticketReservation}
                  paid={ticketReservation.paymentStatus === "COMPLETE"}
                />
              </Suspense>
            )}
            <Divider />
            {false && (
            <Button color="primary" className={classes.textSecondary}>
              {hasCompanions ? "Begleitung ändern" : "Begleitung hinzufügen"}
            </Button>
            )}
          </CardContent>
        </Card>
      </Box>
      {ticketReservation.paymentStatus === "COMPLETE" ? (
        <Box mb={2}>
          <Alert severity="success">
            <b>
              Ticket{ticketReservation.amount > 1 ? "s sind" : " ist"} bereits
              bezahlt
            </b>
          </Alert>
        </Box>
      ) : ticketReservation.amountRedeemed != null ? (
        <Box mb={2}>
          <Alert severity="success">Diese Reservierung wurde eingelöst</Alert>
        </Box>
      ) : ticketReservation.paymentMethod === "offline" ? (
        <>
          <Typography align="center" color="textSecondary" variant="subtitle2">
            Zahle jetzt online, um noch schneller und kontaktlos am Spieltag
            einzuchecken:
          </Typography>
          <Box mt={1} mb={2} style={{ textAlign: "center" }}>
            <TicketPayButton
              gameId={gameId}
              renderButton={(payTicket, loading) => (
                <Button
                  onClick={payTicket}
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  Jetzt online zahlen
                </Button>
              )}
            />
          </Box>
        </>
      ) : null}
      <PageSubHeading primary="Wichtige Hinweise" />
      <TicketReservationHints ticketReservation={ticketReservation} />
    </>
  );
}

export default TicketDetails;
